import { cn } from '@/util/cn';
import { useCallback, useEffect, useState } from 'react';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { localeConfig } from '@/localeConfig';
import { options } from '../HeaderLangSelect/HeaderLangSelect';

// `.lang-select` literal global class on each link is load-bearing —
// the click-outside listener targets it via `closest`.

const wrapperClass = [
  'relative bg-[#363636] pt-1.5 pb-px rounded-md mb-9',
  '[&_span]:pl-[0.425rem]',
].join(' ');

const buttonClass = [
  'flex items-center justify-center',
  'w-[120px] h-[29px] px-0 py-[5px] rounded-md',
  'text-xs text-white cursor-pointer select-none',
  'hover:text-white hover:no-underline',
  '[&_>div]:flex',
  'max-lg:[&_>div_>span]:!block max-lg:[&_>div_>span]:!text-white',
].join(' ');

const dropdownClass = [
  'absolute right-0 -top-[150px] z-[1000] w-[72px]',
  'py-2 px-0 mt-[0.125rem]',
  'text-base text-left list-none text-white',
  'bg-[#212529] bg-clip-padding rounded',
  'border border-black/15',
  'hidden',
  '[&_a]:flex [&_a]:items-center [&_a]:justify-start',
  '[&_a]:h-[35px] [&_a]:w-[71px] [&_a]:pl-[0.53125rem]',
  '[&_a]:text-xs [&_a]:text-white',
  '[&_a]:hover:text-white [&_a]:hover:bg-primary',
  '[&_a>div]:flex',
  '[&_a>div_span]:!block',
].join(' ');

const visibleClass = '!block';

export const SimpleLangSelect = () => {
  const [visible, setVisible] = useState<boolean>(false);

  const links = options.map((o) => (
    <a className="lang-select" key={o.flag} href={localeConfig[o.value as ELocale].websiteDomain}>
      <div>
        {visible && (
          <img
            src={o.flag}
            alt={o.label}
            width={18}
            height={18}
            title={o.title}
          />
        )}
        <span>
          {o.title}
        </span>
      </div>
    </a>
  ));

  const value = options.find((o) => o.value === getLocale())!;

  const hideLangSelect = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const showLangSelect = useCallback(() => {
    setVisible(true);
  }, [setVisible, hideLangSelect]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // If clicked outside of the dropdown
      if (!target.closest('.lang-select')) {
        hideLangSelect();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [visible, hideLangSelect]);

  return (
    <div className={wrapperClass}>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions */}
      <a
        onClick={showLangSelect}
        className={buttonClass}
      >
        <div>
          <img
            src={value.flag}
            alt={value.label}
            width={18}
            height={18}
            title={value.title}
          />
          <span>
            {value.title}
          </span>
        </div>
      </a>
      <div className={cn(dropdownClass, visible && visibleClass)}>
        {links}
      </div>
    </div>
  );
};
